
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import axios from 'axios';
import { API_BASE_URL } from '@/config/api';
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

interface PersonalInfoSectionProps {
  user: any;
  onSave: (data: { first_name: string; last_name: string; email: string; password?: string }) => void;
}

export const PersonalInfoSection = ({ user, onSave }: PersonalInfoSectionProps) => {
  const { t } = useTranslation();
  const { updateUser } = useAuth();
  const [formData, setFormData] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    email: user?.email || '',
    password: ''
  });
  const [errors, setErrors] = useState<string[]>([]);

  // Reset form data whenever user prop changes (when dialog opens)
  useEffect(() => {
    setFormData({
      first_name: user?.first_name || '',
      last_name: user?.last_name || '',
      email: user?.email || '',
      password: ''
    });
    setErrors([]);
  }, [user]);

  // Mutation for saving user data
  const saveUserMutation = useMutation({
    mutationFn: async (userData: any) => {
      const response = await axios.put(
        `${API_BASE_URL}/users/${user?.id}`,
        userData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );
      return response.data;
    },
    onSuccess: (data) => {
      console.log('User updated successfully:', data);
      toast.success(t('personal_info_saved_successfully') || 'Personal information saved successfully');
      updateUser(data);
      onSave(data);
      // Clear password field after successful save
      setFormData(prev => ({ ...prev, password: '' }));
    },
    onError: (error) => {
      console.error('Error saving user data:', error);
      toast.error(t('error_saving_personal_info') || 'Error saving personal information');
    }
  });

  const validateForm = () => {
    const newErrors: string[] = [];

    if (!formData.first_name.trim()) {
      newErrors.push("The name is required");
    }

    if (!formData.last_name.trim()) {
      newErrors.push("The last name is required");
    }

    if (!formData.email.trim()) {
      newErrors.push("The email is required");
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.push("The email is not valid");
    }

    // Only validate password if it's provided
    if (formData.password && formData.password.length < 6) {
      newErrors.push("Password must be at least 6 characters");
    }

    setErrors(newErrors);
    return newErrors.length === 0;
  };

  const handleSave = () => {
    if (validateForm()) {
      const saveData: any = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        microphone: user?.microphone || 'default'
      };

      // Only include password if it's provided
      if (formData.password) {
        saveData.password = formData.password;
      }

      console.log('Saving user data:', saveData);
      saveUserMutation.mutate(saveData);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-gray-900">{t('personal_information')}</h2>
      
      {errors.length > 0 && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative">
          <ul>
            {errors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <Label htmlFor="first_name">{t('name') || 'Name'}:</Label>
          <Input 
            id="first_name"
            value={formData.first_name}
            onChange={(e) => handleInputChange('first_name', e.target.value)}
            placeholder="Juan" 
          />
        </div>
        <div>
          <Label htmlFor="last_name">{t('last_name') || 'Last Name'}:</Label>
          <Input 
            id="last_name"
            value={formData.last_name}
            onChange={(e) => handleInputChange('last_name', e.target.value)}
            placeholder="Pérez" 
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <Label htmlFor="email">{t('email') || 'Email'}:</Label>
          <Input 
            id="email" 
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            placeholder="juan@example.com" 
            disabled={user?.provider === 'google'}
          />
        </div>
        <div>
          <Label htmlFor="password">{t('password') || 'Password'}:</Label>
          <div className="flex items-center gap-2">
            <Input 
              id="password" 
              type="password"
              value={formData.password}
              onChange={(e) => handleInputChange('password', e.target.value)}
              placeholder="••••••••" 
              disabled={user?.provider === 'google'}
            />
            <Button 
              onClick={handleSave} 
              variant="outline" 
              className="bg-green-600 text-white hover:bg-green-700"
              disabled={saveUserMutation.isPending}
            >
              {saveUserMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('saving') || 'Saving...'}
                </>
              ) : (
                t('save')
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
